import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const POSTER_WIDTH = 576;
const POSTER_HEIGHT = 1024;
const EXPORT_SCALE = 1.875;

/** Physical flyer: 21 ft × 8 ft (page size in PDF inches). */
export const FLYER_PDF_WIDTH_IN = 21 * 12; // 252
export const FLYER_PDF_HEIGHT_IN = 8 * 12; // 96
/**
 * PDF MediaBox max is 14,400 user units ≈ 200 in at UserUnit 1.
 * Scale UserUnit so a true 21×8 ft page still fits under that limit.
 */
export const FLYER_PDF_USER_UNIT = Math.max(
  1,
  Math.ceil(Math.max(FLYER_PDF_WIDTH_IN, FLYER_PDF_HEIGHT_IN) / 200)
);
/** html2canvas scale for print PDF (2100×800 → 6300×2400 @ 3×). */
export const FLYER_PDF_CAPTURE_SCALE = 3;
export const FLYER_PDF_JPEG_QUALITY = 0.93;

function waitForImages(root) {
  const imgs = [...root.querySelectorAll('img')];
  return Promise.all(
    imgs.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete && img.naturalWidth) {
            resolve();
            return;
          }
          const done = () => resolve();
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
          setTimeout(done, 2500);
        })
    )
  );
}

async function waitForFonts() {
  try {
    if (document.fonts?.ready) {
      await Promise.race([
        document.fonts.ready,
        new Promise((r) => setTimeout(r, 3000))
      ]);
    }
  } catch {
    /* ignore */
  }
}

/**
 * Render a poster DOM node to a canvas.
 * Portrait cards default to 576×1024 @ EXPORT_SCALE.
 * Wide flyers read data-poster-width / data-poster-height (export scale 1 unless overridden).
 * @param {{ scale?: number }} [options]
 */
export async function capturePosterCanvas(el, options = {}) {
  if (!el) throw new Error('Poster element missing');

  const customW = Number(el.dataset?.posterWidth);
  const customH = Number(el.dataset?.posterHeight);
  const width = Number.isFinite(customW) && customW > 0 ? customW : POSTER_WIDTH;
  const height = Number.isFinite(customH) && customH > 0 ? customH : POSTER_HEIGHT;
  const isCustomSize = width !== POSTER_WIDTH || height !== POSTER_HEIGHT;
  const scale = Number.isFinite(options.scale) && options.scale > 0
    ? options.scale
    : (isCustomSize ? 1 : EXPORT_SCALE);

  const host = document.createElement('div');
  host.setAttribute('data-poster-export-host', '1');
  Object.assign(host.style, {
    position: 'fixed',
    left: '0',
    top: '0',
    width: `${width}px`,
    height: `${height}px`,
    zIndex: '2147483646',
    background: '#ffffff',
    overflow: 'hidden',
    pointerEvents: 'none',
    margin: '0',
    padding: '0'
  });

  const clone = el.cloneNode(true);
  clone.style.transform = 'none';
  clone.style.margin = '0';
  clone.style.position = 'relative';
  clone.style.left = '0';
  clone.style.top = '0';
  clone.style.width = `${width}px`;
  clone.style.height = `${height}px`;
  clone.style.background = isCustomSize ? '#071428' : '#ffffff';
  const exam = clone.querySelector('.br-poster-exam');
  if (exam) exam.remove();
  // Don't bake in “adjust photos” chrome
  clone.classList?.remove?.('is-editing-photos');
  clone.querySelectorAll?.('.br-flyer-photo.is-editable').forEach((node) => {
    node.classList.remove('is-editable');
    node.removeAttribute('role');
    node.removeAttribute('tabindex');
    node.removeAttribute('title');
  });

  host.appendChild(clone);
  document.body.appendChild(host);

  try {
    await waitForFonts();
    await waitForImages(clone);
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    return html2canvas(clone, {
      backgroundColor: isCustomSize ? '#071428' : '#ffffff',
      scale,
      logging: false,
      useCORS: true,
      allowTaint: false,
      width,
      height,
      windowWidth: width,
      windowHeight: height,
      scrollX: 0,
      scrollY: 0,
      x: 0,
      y: 0
    });
  } finally {
    host.remove();
  }
}

/**
 * Export only the poster on a white page (no app chrome, no scaled preview).
 */
export async function capturePosterElement(el) {
  const canvas = await capturePosterCanvas(el);
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Empty poster capture'))), 'image/png');
  });
  return blob;
}

/** Same capture as download, returned as an object URL for on-screen preview. */
export async function capturePosterObjectUrl(el) {
  const blob = await capturePosterElement(el);
  return { blob, url: URL.createObjectURL(blob) };
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export function posterFilename(row) {
  const roll = String(row?.roll_no || row?.id || 'student').replace(/\D/g, '') || 'student';
  const name = String(row?.student_name || '')
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '_')
    .slice(0, 40);
  return `poster_${roll}${name ? `_${name}` : ''}.png`;
}

export function top5PosterFilename({ level, part, year, groupLabel } = {}) {
  return leaderboardPosterFilename(5, { level, part, year, groupLabel });
}

export function top12PosterFilename({ level, part, year, groupLabel } = {}) {
  return leaderboardPosterFilename(12, { level, part, year, groupLabel });
}

export function top20PosterFilename({ level, part, year, groupLabel } = {}) {
  return leaderboardPosterFilename(20, { level, part, year, groupLabel });
}

export function flyerPosterFilename({ level, part, year, groupLabel } = {}) {
  const bits = [groupLabel, level, part, year]
    .map((v) => String(v || '').trim().replace(/[^\w-]+/g, '_'))
    .filter(Boolean);
  return `flyer_21x8ft_80pct${bits.length ? `_${bits.join('_')}` : ''}.png`;
}

export function flyerPdfFilename(meta = {}) {
  return flyerPosterFilename(meta).replace(/\.png$/i, '.pdf');
}

/**
 * Capture the 21×8 ft flyer at high resolution and save a print-ready PDF
 * with a true 21 ft × 8 ft page (252×96 in via PDF UserUnit scaling).
 */
export async function exportFlyerPdf(
  el,
  filename,
  {
    scale = FLYER_PDF_CAPTURE_SCALE,
    jpegQuality = FLYER_PDF_JPEG_QUALITY
  } = {}
) {
  if (!el) throw new Error('Flyer element missing');

  let canvas;
  try {
    canvas = await capturePosterCanvas(el, { scale });
  } catch (err) {
    if (scale > 2) {
      canvas = await capturePosterCanvas(el, { scale: 2 });
    } else {
      throw err;
    }
  }

  const imgData = canvas.toDataURL('image/jpeg', jpegQuality);
  // jsPDF clamps MediaBox to 14,400 units (~200 in). Use UserUnit so the
  // coordinate page stays under that limit while printing at full 21×8 ft.
  const userUnit = FLYER_PDF_USER_UNIT;
  const pageW = FLYER_PDF_WIDTH_IN / userUnit;
  const pageH = FLYER_PDF_HEIGHT_IN / userUnit;
  const pdf = new jsPDF({
    orientation: pageW >= pageH ? 'landscape' : 'portrait',
    unit: 'in',
    format: [pageW, pageH],
    userUnit,
    compress: true
  });

  pdf.addImage(
    imgData,
    'JPEG',
    0,
    0,
    pageW,
    pageH,
    undefined,
    'FAST'
  );
  pdf.save(filename || 'flyer_21x8ft.pdf');
}

function leaderboardPosterFilename(count, { level, part, year, groupLabel } = {}) {
  const bits = [groupLabel, level, part, year]
    .map((v) => String(v || '').trim().replace(/[^\w-]+/g, '_'))
    .filter(Boolean);
  return `poster_top${count}${bits.length ? `_${bits.join('_')}` : ''}.png`;
}

/**
 * Fetch student photo from backend as same-origin blob URL (for html2canvas).
 */
export async function fetchPosterPhoto(apiBaseUrl, authHeaders, rowId) {
  if (!rowId) return null;
  const res = await fetch(`${apiBaseUrl}/board-results/${rowId}/photo`, {
    headers: authHeaders()
  });
  if (!res.ok) return null;
  const blob = await res.blob();
  if (!blob?.size || !/^image\//i.test(blob.type || 'image/jpeg')) return null;
  return URL.createObjectURL(blob);
}

/**
 * Fetch many poster photos in parallel (bounded concurrency).
 * Calls onPhoto(rowId, url) as each succeeds for progressive UI updates.
 */
export async function fetchPosterPhotosParallel(
  apiBaseUrl,
  authHeaders,
  rowIds,
  { concurrency = 8, onPhoto = null } = {}
) {
  const ids = [...new Set((rowIds || []).filter(Boolean))];
  if (!ids.length || !apiBaseUrl) return {};

  const results = {};
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < ids.length) {
      const rowId = ids[nextIndex];
      nextIndex += 1;
      try {
        const url = await fetchPosterPhoto(apiBaseUrl, authHeaders, rowId);
        if (url) {
          results[rowId] = url;
          onPhoto?.(rowId, url);
        }
      } catch {
        /* skip failed photo */
      }
    }
  }

  const workers = Array.from(
    { length: Math.min(concurrency, ids.length) },
    () => worker()
  );
  await Promise.all(workers);
  return results;
}

export async function uploadPosterPhoto(apiBaseUrl, authHeaders, rowId, file) {
  if (!rowId || !file) throw new Error('Photo file missing');
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${apiBaseUrl}/board-results/${rowId}/photo`, {
    method: 'PUT',
    headers: authHeaders(),
    body: form
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message || 'Could not replace photo');
  }
  return fetchPosterPhoto(apiBaseUrl, authHeaders, rowId);
}

export function revokePhotoUrl(url) {
  if (url && url.startsWith('blob:')) {
    try {
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  }
}
